import React, { useCallback } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';
import { UnknownObject } from '../../../../../../react-app-env';

import { Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';

import { Patient, validation, ValidationErrors, PatientReducerAction } from '../../patient';

import styles from './styles';
import PatientNhsAutocomplete from '../../../../../../components/PatientNhsAutocomplete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import SelectedPatientSummary from './components/PatientNhsSummary';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  patient: Patient;
  errors: ValidationErrors;
  isUnknownNhs: boolean;
  setIsUnknownNhs: React.Dispatch<React.SetStateAction<boolean>>;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  dispatch: React.Dispatch<{ type: PatientReducerAction; value: any }>;
}

const StepPatient = ({ classes, patient, errors, isUnknownNhs, setIsUnknownNhs, setErrors, dispatch }: Props): React.ReactElement => {
  const validate = useCallback(
    (field: string, value?: any) => {
      const errorItems: UnknownObject = {};

      // Only validate nhs_number if it's not marked as unknown
      if (field === 'nhs_number') {
        errorItems.nhs_number = isUnknownNhs ? false : validation.nhs_number(value || patient.nhs_number);
      }

      if (field === 'mrn') {
        errorItems.mrn = validation.mrn(value || patient.mrn);
      }
      if (field === 'first_name') {
        errorItems.first_name = validation.first_name(value || patient.first_name);
      }
      if (field === 'last_name') {
        errorItems.last_name = validation.last_name(value || patient.last_name);
      }
      if (field === 'dob') {
        errorItems.dob = validation.dob(value || patient.dob);
      }

      setErrors((prev) => ({ ...prev, ...errorItems }));
      return errorItems;
    },
    [patient, isUnknownNhs, setErrors]
  );

  return (
    <div className={classes.root}>
      <Typography className={classes.header} variant="h4">
        Patient
      </Typography>

      {(!patient.nhs_number || !patient.first_name || !patient.last_name || !patient.dob) && (
        <PatientNhsAutocomplete
          validate={validate}
          errors={errors}
          isUnknownNhs={isUnknownNhs}
          onPatientSelect={(patient) => {
            dispatch({ type: PatientReducerAction.NHS_NUMBER, value: patient.nhs_number });
            dispatch({ type: PatientReducerAction.FIRST_NAME, value: patient.first_name });
            dispatch({ type: PatientReducerAction.LAST_NAME, value: patient.last_name });
            dispatch({ type: PatientReducerAction.DOB, value: patient.dob });
            dispatch({ type: PatientReducerAction.GENDER, value: patient.gender });
          }}
          setErrors={setErrors}
        />
      )}

      {!isUnknownNhs && patient.nhs_number && patient.first_name && patient.last_name && patient.dob && (
        <SelectedPatientSummary
          nhsNumber={patient.nhs_number}
          name={`${patient.first_name} ${patient.last_name}`}
          dob={patient.dob as Date}
          onClear={() => {
            dispatch({ type: PatientReducerAction.NHS_NUMBER, value: '' });
            dispatch({ type: PatientReducerAction.FIRST_NAME, value: '' });
            dispatch({ type: PatientReducerAction.LAST_NAME, value: '' });
            dispatch({ type: PatientReducerAction.DOB, value: '' });
            dispatch({ type: PatientReducerAction.GENDER, value: '' });
          }}
        />
      )}

      <FormControlLabel
        sx={{ py: 1 }}
        control={
          <Checkbox
            checked={isUnknownNhs}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsUnknownNhs(checked);
              if (checked) {
                dispatch({ type: PatientReducerAction.NHS_NUMBER, value: '' });
                // Clear any NHS number validation error
                setErrors((prev) => ({ ...prev, nhs_number: '' }));
              }
            }}
          />
        }
        label="Patient does not have an NHS number or it is unknown."
      />

      <TextField
        className={classes.textField}
        data-qa="mrn-textfield"
        required
        label="Medical Record Number"
        variant="outlined"
        error={!!errors.mrn}
        helperText={errors.mrn}
        value={patient.mrn}
        onChange={(e) => dispatch({ type: PatientReducerAction.MRN, value: e.target.value })}
        onKeyUp={() => validate('mrn')}
        onBlur={() => validate('mrn')}
      />

      {isUnknownNhs && (
        <>
          <TextField
            className={classes.textField}
            data-qa="first_name-textfield"
            required
            label="First name"
            variant="outlined"
            error={!!errors.first_name}
            helperText={errors.first_name}
            value={patient.first_name}
            onChange={(e) => dispatch({ type: PatientReducerAction.FIRST_NAME, value: e.target.value })}
            onKeyUp={() => validate('first_name')}
            onBlur={() => validate('first_name')}
          />
          <TextField
            className={classes.textField}
            data-qa="last_name-textfield"
            required
            label="Last name"
            variant="outlined"
            error={!!errors.last_name}
            helperText={errors.last_name}
            value={patient.last_name}
            onChange={(e) => dispatch({ type: PatientReducerAction.LAST_NAME, value: e.target.value })}
            onKeyUp={() => validate('last_name')}
            onBlur={() => validate('last_name')}
          />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Date of birth"
              value={patient.dob ? moment(patient.dob) : null}
              inputFormat="DD/MM/YYYY"
              onChange={(newValue) => {
                dispatch({ type: PatientReducerAction.DOB, value: newValue?.toDate() });
                validate('dob', newValue?.toDate());
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  className={classes.textField}
                  error={!!errors.dob}
                  helperText={errors.dob}
                  onKeyUp={() => validate('dob')}
                  onBlur={() => validate('dob')}
                />
              )}
            />
          </LocalizationProvider>
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(StepPatient);
